// MfaEnforcementGate — mandatory authenticator (TOTP) setup for any signed-in
// user who has no TOTP activated. The Cognito pool runs MFA=OPTIONAL (required
// to allow passkeys as a first factor); this gate guarantees every user still
// holds TOTP, so a password sign-in always carries a second factor and no login
// is ever single-factor. Passkeys are additive on top of this.
//
// It blocks the app (full-screen) until setup completes; the only ways out are
// finishing setup or signing out.
import React, { useState, useEffect, useCallback } from 'react';
import QRCode from 'qrcode';
import {
  fetchMFAPreference,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
} from 'aws-amplify/auth';
import { useFileContext } from '../../ContextAPI/FileContext';
import { buildOtpauthUri } from '../../utils/otpauth';
import './MfaEnforcementGate.css';

const MfaEnforcementGate = () => {
  const { auth, userEmail, logout } = useFileContext();
  const [needsSetup, setNeedsSetup] = useState(false);
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [starting, setStarting] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // When authenticated, check whether TOTP is activated for this user.
  useEffect(() => {
    let cancelled = false;
    if (!auth) { setNeedsSetup(false); return undefined; }
    (async () => {
      try {
        const pref = await fetchMFAPreference();
        const hasTotp = (pref?.enabled || []).includes('TOTP');
        if (!cancelled) setNeedsSetup(!hasTotp);
      } catch (e) {
        // Fail open on a transient error rather than trap the user; the next
        // login re-checks. (Logged for visibility.)
        console.warn('MFA preference check failed:', e?.message || e);
        if (!cancelled) setNeedsSetup(false);
      }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  // Begin TOTP setup (fetch a fresh secret + render the QR) once we know it's needed.
  const beginSetup = useCallback(async () => {
    setStarting(true);
    setError('');
    try {
      const totp = await setUpTOTP();
      const sharedSecret = totp.sharedSecret;
      setSecret(sharedSecret);
      const uri = buildOtpauthUri({
        secret: sharedSecret,
        email: userEmail || 'account',
        issuer: 'RDV Systems',
      });
      const dataUrl = await QRCode.toDataURL(uri, { width: 200, margin: 2 });
      setQr(dataUrl);
    } catch (e) {
      console.warn('setUpTOTP failed:', e?.message || e);
      setError('Could not start authenticator setup. Please try again.');
    } finally {
      setStarting(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (needsSetup && !secret && !starting) beginSetup();
  }, [needsSetup, secret, starting, beginSetup]);

  const handleVerify = async () => {
    if (code.length !== 6) { setError('Enter the 6-digit code from your authenticator.'); return; }
    setBusy(true);
    setError('');
    try {
      await verifyTOTPSetup({ code });
      // Explicitly activate TOTP as the preferred MFA (don't rely on auto-activation).
      await updateMFAPreference({ totp: 'PREFERRED' });
      setNeedsSetup(false); // unlock the app
    } catch (e) {
      console.warn('verifyTOTPSetup failed:', e?.name || e?.message || e);
      setError('That code was incorrect or expired. Enter the current 6-digit code.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  if (!auth || !needsSetup) return null;

  return (
    <div className="mfa-gate__backdrop">
      <div className="mfa-gate" role="dialog" aria-modal="true" aria-label="Set up two-factor authentication">
        <h2 className="mfa-gate__title">Secure your account</h2>
        <p className="mfa-gate__subtitle">
          Two-factor authentication is required. Scan this with an authenticator app
          (Google Authenticator, Authy, 1Password…), then enter the 6-digit code it shows.
        </p>

        <div className="mfa-gate__qr">
          {qr ? (
            <img src={qr} alt="Authenticator QR code" />
          ) : (
            <div className="mfa-gate__qr-placeholder">{starting ? 'Generating…' : ' '}</div>
          )}
        </div>

        {secret && (
          <p className="mfa-gate__secret">
            Can’t scan? Enter this key:<br />
            <code>{secret}</code>
          </p>
        )}

        <input
          className="mfa-gate__code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); if (error) setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) handleVerify(); }}
          autoFocus
        />

        {error && <p className="mfa-gate__error">{error}</p>}

        <button
          type="button"
          className="mfa-gate__verify"
          onClick={handleVerify}
          disabled={busy || code.length !== 6 || !secret}
        >
          {busy ? 'Verifying…' : 'Verify & continue'}
        </button>

        <button type="button" className="mfa-gate__signout" onClick={logout}>
          Sign out instead
        </button>
      </div>
    </div>
  );
};

export default MfaEnforcementGate;
